/**
 * API Constants - Centralized API endpoints and status codes
 */

// API Base Configuration
export const API_CONFIG = {
	BASE_URL: import.meta.env.VITE_API_URL || "http://localhost:20892",
	TIMEOUT: 10000,
}

// Authentication Endpoints
export const AUTH_ENDPOINTS = {
	LOGIN: "/api/auth/login",
	LOGOUT: "/api/auth/logout",
	FORGOT_PASSWORD: "/api/auth/forgot-password",
	RESET_PASSWORD: "/api/auth/reset-password",
	VERIFY_2FA: "/api/auth/verify2FA",
	RESEND_2FA: "/api/auth/resend2FA",
	CURRENT_USER: "/api/users/current",
	ENABLE_2FA: "/api/users/current/two-factor?state=true",
	DISABLE_2FA: "/api/users/current/two-factor?state=false",
}

// Backoffice EVSE / station endpoints (OCPP charge-points vs CEME OCPI EVSE)
export const CHARGE_POINT_EVSE_ENDPOINTS = {
	LIST: "/api/backoffice/charge-points/list",
	OVERVIEW: "/api/backoffice/charge-points/overview",
	DETAIL: (id) => `/api/backoffice/charge-points/${id}`,
	LOCATIONS: "/api/backoffice/charge-points/locations",
	LOCATIONS_WITH_EVSES: "/api/backoffice/charge-points/locations",
}

export const OCPI_EVSE_ENDPOINTS = {
	LIST: "/api/backoffice/evse/list",
	OVERVIEW: "/api/backoffice/evse/overview",
	DETAIL: (id) => `/api/backoffice/evse/${id}`,
	UPDATE: (id) => `/api/backoffice/evse/${id}`,
	LOCATIONS: "/api/backoffice/evse/locations",
	LOCATIONS_WITH_EVSES: "/api/backoffice/evse/locations",
	LOCATIONS_MAP: "/api/backoffice/evse/locations/map",
	LOCATION_PARTIES: "/api/backoffice/evse/locations/parties",
	LOCATION_DETAIL: (documentId) => `/api/backoffice/evse/locations/${documentId}`,
}

export const EVSE_ENDPOINTS = CHARGE_POINT_EVSE_ENDPOINTS

// Backoffice Metrics Endpoints
export const METRICS_ENDPOINTS = {
	OVERVIEW: "/api/backoffice/metrics/overview",
	SESSIONS: "/api/backoffice/metrics/sessions",
	ENERGY: "/api/backoffice/metrics/energy",
	BILLING: "/api/backoffice/metrics/billing",
	BALANCE: "/api/backoffice/metrics/balance",
	FAILURES: "/api/backoffice/metrics/failures",
	DASHBOARD_CHARTS: "/api/backoffice/metrics/charts/dashboard",
}

// Backoffice POS Endpoints
export const POS_ENDPOINTS = {
	LIST: "/api/backoffice/pos",
	DETAIL: (documentId) => `/api/backoffice/pos/${documentId}`,
	LOGS: (documentId) => `/api/backoffice/pos/${documentId}/logs`,
	DELETE: (documentId) => `/api/backoffice/pos/${documentId}`,
	UNPAIR: (documentId) => `/api/backoffice/pos/${documentId}/unpair`,
	PAIRING_CLAIM: "/api/pos/pairing/claim",
	PAIRING_STATUS: "/api/pos/pairing/status",
}

// Backoffice Sessions & CDR Endpoints (deprecated, use TRANSACTION_ENDPOINTS)
export const SESSIONS_ENDPOINTS = {
	LIST: "/api/backoffice/sessions",
	DETAIL: (sessionId) => `/api/backoffice/sessions/${sessionId}`,
	CDR_LIST: "/api/backoffice/cdrs",
	CDR_DETAIL: (cdrId) => `/api/backoffice/cdrs/${cdrId}`,
	SEND_COMMAND: "/api/ocpi-command",
}

// Backoffice Transaction Endpoints (OCPP)
export const TRANSACTION_ENDPOINTS = {
	LIST: "/api/sessions/ocpp",
	DETAIL: (id) => `/api/sessions/ocpp/${id}`,
	EXPORT: "/api/sessions/ocpp",
}

// Backoffice OCPI Session Endpoints (CEME ad-hoc/POS transactions)
// No per-session detail route exists; the list response carries every field the UI needs.
export const OCPI_SESSION_ENDPOINTS = {
	LIST: "/api/sessions/ocpi",
	EXPORT: "/api/sessions/ocpi",
}

// Backoffice QR Code Endpoints
export const QR_CODE_ENDPOINTS = {
	LIST_STATUS: "/api/backoffice/evses/qr-status",
	GET: (chargePointId, connectorId) => `/api/backoffice/charge-points/${chargePointId}/qr-code${connectorId ? `?connector_id=${connectorId}` : ""}`,
	REGENERATE: (chargePointId, connectorId) =>
		`/api/backoffice/charge-points/${chargePointId}/qr-code/regenerate${connectorId ? `?connector_id=${connectorId}` : ""}`,
	DOWNLOAD: (chargePointId, connectorId) =>
		`/api/backoffice/charge-points/${chargePointId}/qr-code/download${connectorId ? `?connector_id=${connectorId}` : ""}`,
	BULK_GENERATE: (locationId) => `/api/backoffice/location/${locationId}/qr-codes/bulk-generate`,
}

// Backoffice Location Endpoints
export const LOCATION_ENDPOINTS = {
	LIST: "/api/backoffice/locations",
	CREATE: "/api/backoffice/locations",
	DETAIL: (documentId) => `/api/backoffice/locations/${documentId}`,
	UPDATE: (documentId) => `/api/backoffice/locations/${documentId}`,
	ASSIGN_CHARGE_POINTS: (documentId) => `/api/backoffice/locations/${documentId}/assign-charge-points`,
	BACKFILL_UNASSIGNED: "/api/backoffice/locations/backfill-unassigned",
}

// Operator-wide configuration (Admin)
export const OPERATOR_ENDPOINTS = {
	PREAUTH_CONFIG: "/api/backoffice/operator/preauth-config",
	PUBLIC_BRANDING: "/api/backoffice/operator/public-branding",
	PUBLIC_BRANDING_LOGO: "/api/backoffice/operator/public-branding/logo",
}

// Backoffice CEME Customer Endpoints
export const CEME_CUSTOMER_ENDPOINTS = {
	LIST: "/api/backoffice/ceme-customers",
	DETAIL: (documentId) => `/api/backoffice/ceme-customers/${documentId}`,
	SESSIONS: (documentId) => `/api/backoffice/ceme-customers/${documentId}/sessions`,
	INVOICES: (documentId) => `/api/backoffice/ceme-customers/${documentId}/invoices`,
	MOVEMENTS: (documentId) => `/api/backoffice/ceme-customers/${documentId}/movements`,
	SUSPEND: (documentId) => `/api/backoffice/ceme-customers/${documentId}/suspend`,
	ASSIGN_CREDIT: (documentId) => `/api/backoffice/ceme-customers/${documentId}/assign-credit`,
	TOPUP_CONFIG: (documentId) => `/api/backoffice/ceme-customers/${documentId}/topup-config`,
}

export const PAYMENT_RECORD_ENDPOINTS = {
	UNRECOVERED_SUMMARY: "/api/payment-records/unrecovered-summary",
}

// Backoffice Charge Point Endpoints (OCPP)
export const CHARGE_POINT_ENDPOINTS = {
	LIST: "/api/backoffice/charge-points/list",
	OVERVIEW: "/api/backoffice/charge-points/overview",
	DETAIL: (id) => `/api/backoffice/charge-points/${id}`,
	LOCATIONS: "/api/backoffice/charge-points/locations",
	CONFIGURATION: (id) => `/api/backoffice/charge-points/${id}/configuration`,
	CHANGE_AVAILABILITY: (id) => `/api/backoffice/charge-points/${id}/change-availability`,
	RESET: (id) => `/api/backoffice/charge-points/${id}/reset`,
	CLEAR_CACHE: (id) => `/api/backoffice/charge-points/${id}/clear-cache`,
}

// Backoffice OCPI module endpoints (CEME)
export const OCPI_BACKOFFICE_ENDPOINTS = {
	CREDENTIALS: "/api/backoffice/ocpi-credentials",
	INTEGRATION_STATUS: "/api/backoffice/ocpi/integration-status",
	COMMUNICATIONS: "/api/backoffice/ocpi/communications",
	COMMUNICATIONS_UNIFIED: "/api/backoffice/ocpi/communications/unified",
	REGISTER: "/api/backoffice/ocpi/register",
	UNREGISTER: "/api/backoffice/ocpi/unregister",
	RENEW_TOKEN: "/api/backoffice/ocpi/renew-token",
	HUB_CREDENTIALS: "/api/backoffice/ocpi/hub-credentials",
	PUBLIC_URL: "/api/backoffice/ocpi/public-url",
	OUTBOX: "/api/backoffice/ocpi/outbox",
	OUTBOX_RETRY: (documentId) => `/api/backoffice/ocpi/outbox/${documentId}/retry`,
	TARIFFS: "/api/backoffice/ocpi-tariffs",
	INTEGRITY_LATEST: "/api/integrity-reports/latest",
	INTEGRITY_LIST: "/api/integrity-reports",
	INTEGRITY_RUN_NOW: "/api/integrity-reports/run-now",
}

// Backoffice MOBI.E console endpoints (tokens and commands sent on demand)
export const OCPI_CONSOLE_ENDPOINTS = {
	LOCAL_TOKENS: "/api/ocpi-tokens?limit=100",
	TOKEN_UID: "/api/backoffice/ocpi/tokens/uid",
	TOKEN_CONTRACT_ID: "/api/backoffice/ocpi/tokens/contract-id",
	TOKENS: "/api/backoffice/ocpi/tokens",
	TOKEN: (uid) => `/api/backoffice/ocpi/tokens/${encodeURIComponent(uid)}`,
	COMMAND_START: "/api/backoffice/ocpi/commands/START_SESSION",
	COMMAND_STOP: "/api/backoffice/ocpi/commands/STOP_SESSION",
	COMMAND_UNLOCK: "/api/backoffice/ocpi/commands/UNLOCK_CONNECTOR",
	COMMANDS: "/api/backoffice/ocpi/commands",
	COMMAND_STATUS: (commandId) => `/api/backoffice/ocpi/commands/${encodeURIComponent(commandId)}`,
	LOCATIONS: "/api/backoffice/evse/locations",
	ACTIVE_SESSIONS: "/api/sessions/ocpi?status=ACTIVE&pageSize=100",
	HUB_READ: (module) => `/api/backoffice/ocpi/hub/${module}`,
	HUB_LOCATION: (locationId) => `/api/backoffice/ocpi/hub/locations/${encodeURIComponent(locationId)}`,
}

// Backoffice Tariff Profile Endpoints (OCPP)
export const TARIFF_PROFILE_ENDPOINTS = {
	LIST: "/api/backoffice/tariff-profiles",
	CREATE: "/api/backoffice/tariff-profiles",
	DETAIL: (documentId) => `/api/backoffice/tariff-profiles/${documentId}`,
	UPDATE: (documentId) => `/api/backoffice/tariff-profiles/${documentId}`,
	DELETE: (documentId) => `/api/backoffice/tariff-profiles/${documentId}`,
	ACTIVATE: (documentId) => `/api/backoffice/tariff-profiles/${documentId}/activate`,
	DEACTIVATE: (documentId) => `/api/backoffice/tariff-profiles/${documentId}/deactivate`,
	RESOLVE: "/api/backoffice/tariff-profiles/resolve",
}

// Backoffice eMSP Margin Endpoints (CEME)
export const EMSP_MARGIN_ENDPOINTS = {
	LIST: "/api/backoffice/emsp-margins",
	CREATE: "/api/backoffice/emsp-margins",
	UPDATE: (documentId) => `/api/backoffice/emsp-margins/${documentId}`,
	DELETE: (documentId) => `/api/backoffice/emsp-margins/${documentId}`,
	ACTIVATE: (documentId) => `/api/backoffice/emsp-margins/${documentId}/activate`,
	DEACTIVATE: (documentId) => `/api/backoffice/emsp-margins/${documentId}/deactivate`,
	SIMULATE: "/api/backoffice/emsp-margins/simulate",
}

// Backoffice CEME Price List Endpoints (CEME)
export const CEME_PRICE_LIST_ENDPOINTS = {
	LIST: "/api/backoffice/ceme-price-lists",
	CREATE: "/api/backoffice/ceme-price-lists",
	UPDATE: (documentId) => `/api/backoffice/ceme-price-lists/${documentId}`,
	DELETE: (documentId) => `/api/backoffice/ceme-price-lists/${documentId}`,
	ACTIVATE: (documentId) => `/api/backoffice/ceme-price-lists/${documentId}/activate`,
	DEACTIVATE: (documentId) => `/api/backoffice/ceme-price-lists/${documentId}/deactivate`,
}

// Backoffice Charging Profile Endpoints (OCPP)
export const CHARGING_PROFILE_ENDPOINTS = {
	LIST_ALL: "/api/backoffice/charging-profiles",
	LIST: (chargePointId) => `/api/backoffice/charge-points/${chargePointId}/charging-profiles`,
	CREATE: (chargePointId) => `/api/backoffice/charge-points/${chargePointId}/charging-profiles`,
	CLEAR: (chargePointId, profileId) => `/api/backoffice/charge-points/${chargePointId}/charging-profiles/${profileId}`,
	COMPOSITE_SCHEDULE: (chargePointId) => `/api/backoffice/charge-points/${chargePointId}/composite-schedule`,
}

// Backoffice Charging Profile Template Endpoints
export const CHARGING_PROFILE_TEMPLATE_ENDPOINTS = {
	LIST: "/api/backoffice/charging-profile-templates",
	CREATE: "/api/backoffice/charging-profile-templates",
	UPDATE: (documentId) => `/api/backoffice/charging-profile-templates/${documentId}`,
	DELETE: (documentId) => `/api/backoffice/charging-profile-templates/${documentId}`,
	ACTIVATE: (documentId) => `/api/backoffice/charging-profile-templates/${documentId}/activate`,
	DEACTIVATE: (documentId) => `/api/backoffice/charging-profile-templates/${documentId}/deactivate`,
	INSTANCES: (documentId) => `/api/backoffice/charging-profile-templates/${documentId}/instances`,
}

// Backoffice Remote Commands Endpoints (OCPP)
export const REMOTE_COMMANDS_ENDPOINTS = {
	CHANGE_AVAILABILITY: (chargePointId) => `/api/backoffice/charge-points/${chargePointId}/change-availability`,
	RESET: (chargePointId) => `/api/backoffice/charge-points/${chargePointId}/reset`,
	CLEAR_CACHE: (chargePointId) => `/api/backoffice/charge-points/${chargePointId}/clear-cache`,
	START_CHARGING: (chargePointId) => `/api/backoffice/charge-points/${chargePointId}/start-charging`,
	STOP_CHARGING: (chargePointId) => `/api/backoffice/charge-points/${chargePointId}/stop-charging`,
	GET_CONFIGURATION: (chargePointId) => `/api/backoffice/charge-points/${chargePointId}/configuration`,
	UPDATE_CONFIGURATION: (chargePointId) => `/api/backoffice/charge-points/${chargePointId}/configuration`,
	REQUEST_DIAGNOSTICS: (chargePointId) => `/api/backoffice/charge-points/${chargePointId}/diagnostics`,
}

// HTTP Status Codes
export const HTTP_STATUS = {
	OK: 200,
	CREATED: 201,
	ACCEPTED: 202,
	NO_CONTENT: 204,
	BAD_REQUEST: 400,
	UNAUTHORIZED: 401,
	FORBIDDEN: 403,
	NOT_FOUND: 404,
	CONFLICT: 409,
	UNPROCESSABLE_ENTITY: 422,
	INTERNAL_SERVER_ERROR: 500,
	SERVICE_UNAVAILABLE: 503,
}

// Token Storage Keys
export const TOKEN_STORAGE = {
	KEY: "authToken",
	EXPIRATION_BUFFER: 60000, // 1 minute buffer before actual expiration
}

// Common Query Parameters
export const QUERY_PARAMS = {
	FILTER: "filter",
	SEARCH: "search",
	PAGE: "page",
	LIMIT: "limit",
	SORT: "sort",
	ORDER: "order",
	STATUS: "status",
	OPERATOR_ID: "operatorId",
	LOCATION_ID: "locationId",
	TIME_RANGE: "timeRange",
	START_DATE: "startDate",
	END_DATE: "endDate",
}
